import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, NgZone, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { Product, ProductStats } from '../../../core/models/product.model';
import { ProductService } from '../../../core/services/product.service';
import { ProductFormModalComponent } from './product-form-modal.component';

@Component({
  selector: 'app-products-dashboard',
  standalone: true,
  imports: [CommonModule, ProductFormModalComponent],
  templateUrl: './products-dashboard.component.html',
  styleUrls: ['./products-dashboard.component.scss'],
})
export class ProductsDashboardComponent implements OnInit, OnDestroy {
  products: Product[] = [];
  filteredProducts: Product[] = [];
  stats: ProductStats = {
    totalProducts: 0,
    parentProducts: 0,
    childProducts: 0,
    categories: 0,
  };
  editingProduct: Product | null = null;
  deletingProduct: Product | null = null;
  isFormOpen = false;
  isLoading = true;
  isSaving = false;
  isDeleting = false;
  errorMessage = '';
  readonly pageSize = 5;
  currentPage = 1;
  private productsSubscription?: Subscription;
  private loadRetryTimeout?: number;

  constructor(
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly ngZone: NgZone,
    private readonly productService: ProductService,
  ) {}

  ngOnInit(): void {
    this.loadProducts();

    this.loadRetryTimeout = window.setTimeout(() => {
      if (this.isLoading && !this.products.length) {
        this.loadProducts();
      }
    }, 750);
  }

  loadProducts(): void {
    this.isLoading = true;
    this.errorMessage = '';
    this.productsSubscription?.unsubscribe();

    try {
      this.productsSubscription = this.productService.getProducts().subscribe({
      next: (products) => {
        this.ngZone.run(() => {
          this.products = products || [];
          this.filteredProducts = [...this.products];
          this.updateStats(this.products);
          this.currentPage = Math.min(this.currentPage, this.totalPages);
          this.isLoading = false;
          this.errorMessage = '';
          this.changeDetectorRef.detectChanges();
        });
      },
      error: (error: unknown) => {
        this.ngZone.run(() => {
          console.error('[Dashboard Products Error]', error);
          this.errorMessage = this.getErrorMessage(error, 'Unable to load products.');
          this.loadPublicProductsFallback();
          this.changeDetectorRef.detectChanges();
        });
      },
      });
    } catch (error) {
      console.error('[Dashboard Products Setup Error]', error);
      this.products = [];
      this.filteredProducts = [];
      this.updateStats([]);
      this.isLoading = false;
      this.errorMessage = this.getErrorMessage(error, 'Unable to load products.');
      this.changeDetectorRef.detectChanges();
    }
  }

  get parentProducts(): Product[] {
    return this.products
      .filter((product) => product.type === 'parent')
      .sort((a, b) => a.displayOrder - b.displayOrder || a.name.localeCompare(b.name));
  }

  get activeParentProducts(): Product[] {
    return this.parentProducts.filter((product) => product.status === 'active');
  }

  get nextDisplayOrder(): number {
    return this.products.length + 1;
  }

  get paginatedParents(): Product[] {
    const startIndex = (this.currentPage - 1) * this.pageSize;
    const endIndex = startIndex + this.pageSize;

    return this.parentProducts.slice(startIndex, endIndex);
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.parentProducts.length / this.pageSize));
  }

  get showingStart(): number {
    if (!this.parentProducts.length) {
      return 0;
    }

    return (this.currentPage - 1) * this.pageSize + 1;
  }

  get showingEnd(): number {
    return Math.min(this.currentPage * this.pageSize, this.parentProducts.length);
  }

  openAddProduct(): void {
    this.editingProduct = null;
    this.isFormOpen = true;
  }

  openEditProduct(product: Product): void {
    this.editingProduct = product;
    this.isFormOpen = true;
  }

  closeForm(): void {
    this.editingProduct = null;
    this.isFormOpen = false;
  }

  async saveProduct(product: Product): Promise<void> {
    this.isSaving = true;
    this.errorMessage = '';

    try {
      if (this.editingProduct) {
        await this.productService.updateProduct(product.id, product);
      } else {
        await this.productService.addProduct(product);
      }

      this.closeForm();
    } catch (error) {
      this.errorMessage = this.getErrorMessage(error, 'Unable to save product.');
    } finally {
      this.isSaving = false;
    }
  }

  confirmDelete(product: Product): void {
    this.deletingProduct = product;
  }

  cancelDelete(): void {
    this.deletingProduct = null;
  }

  async deleteProduct(): Promise<void> {
    if (!this.deletingProduct) {
      return;
    }

    const productToDelete = this.deletingProduct;
    this.isDeleting = true;
    this.errorMessage = '';
    this.deletingProduct = null;
    this.products = this.products.filter((product) => product.id !== productToDelete.id);
    this.filteredProducts = [...this.products];
    this.updateStats(this.products);
    this.currentPage = Math.min(this.currentPage, this.totalPages);
    this.changeDetectorRef.detectChanges();

    try {
      await this.productService.deleteProduct(productToDelete.id);
    } catch (error) {
      this.errorMessage = this.getErrorMessage(error, 'Unable to delete product.');
    } finally {
      this.isDeleting = false;
      this.changeDetectorRef.detectChanges();
    }
  }

  goToPreviousPage(): void {
    this.currentPage = Math.max(1, this.currentPage - 1);
  }

  goToNextPage(): void {
    this.currentPage = Math.min(this.totalPages, this.currentPage + 1);
  }

  getChildProducts(parentId: string): Product[] {
    return this.products
      .filter((product) => product.type === 'child' && product.parentId === parentId)
      .sort((a, b) => a.displayOrder - b.displayOrder || a.name.localeCompare(b.name));
  }

  formatDate(value: string): string {
    return new Intl.DateTimeFormat('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }).format(new Date(value));
  }

  ngOnDestroy(): void {
    if (this.loadRetryTimeout) {
      window.clearTimeout(this.loadRetryTimeout);
    }

    this.productsSubscription?.unsubscribe();
  }

  trackByProduct(_: number, product: Product): string {
    return product.id;
  }

  private getErrorMessage(error: unknown, fallback: string): string {
    if (error instanceof Error && error.message) {
      return error.message;
    }

    return fallback;
  }

  private updateStats(products: Product[]): void {
    this.stats = this.productService.getStats(products);
  }

  private loadPublicProductsFallback(): void {
    this.productsSubscription?.unsubscribe();

    this.productsSubscription = this.productService.getActiveParentProducts().subscribe({
      next: (products) => {
        this.ngZone.run(() => {
          this.products = products || [];
          this.filteredProducts = [...this.products];
          this.updateStats(this.products);
          this.currentPage = Math.min(this.currentPage, this.totalPages);
          this.isLoading = false;
          this.errorMessage = this.products.length
            ? 'Showing public active products. Log in with an authorized admin session to manage all products.'
            : 'Unable to load admin products. Log in again with an authorized admin session.';
          this.changeDetectorRef.detectChanges();
        });
      },
      error: (fallbackError: unknown) => {
        this.ngZone.run(() => {
          console.error('[Dashboard Products Public Fallback Error]', fallbackError);
          this.products = [];
          this.filteredProducts = [];
          this.updateStats([]);
          this.isLoading = false;
          this.errorMessage = this.getErrorMessage(fallbackError, 'Unable to load products.');
          this.changeDetectorRef.detectChanges();
        });
      },
    });
  }
}
